import { CopyOutlined, DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { Dropdown, Menu, message, Typography } from "antd";
import ListTable from "common/ListTable";
import { DAILY_LINE_PLAN_LIST_COPY, ImportDailyLinePlan } from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  DAILY_LINE_PLAN_IMPORT_PERMISSION,
  NO_PERMISSION_REQUIRED,
} from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";
import "./index.css";
import moment from "moment";
import { alertPop } from "apiServices/common/helper";
const DailyFinishingLinePlan = () => {
  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [filterValues, setFilterValues] = useState({});

  const DAILY_LINE_PLAN_LIST_API =
    "/api/digitalization/daily-finishing-line-plan-list";

  const DAILY_LINE_PLAN_DELETE_API =
    "/api/digitalization/daily-finishing-line-plan-delete";

  const refetch = () => {
    setRefresh((prev) => prev + 1);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
  };

  const isCurrentDate = (dateString) => {
    if (!dateString) return false;
    const recordDate = moment(dateString).format("YYYY-MM-DD");
    const today = moment().format("YYYY-MM-DD");
    return recordDate === today;
  };

  const getAppRoleMenu = useCallback(async () => {
    setLoading(true);
    setError(null);

    const query = `${DAILY_LINE_PLAN_LIST_API}`;
    const bodyData = {
      page: currentPage,
      ...(filterValues?.style_id && { style_id: filterValues?.style_id }),
      ...(filterValues?.line_id && { line_id: filterValues?.line_id }),
      ...(filterValues?.buyer_id && { buyer_id: filterValues?.buyer_id }),
      ...(filterValues?.sub_style_id && {
        sub_style_id: filterValues?.sub_style_id,
      }),
    };

    const response = await getData(query, false, bodyData);
    if (response?.status === 200) {
      setLoading(false);
      setMetaData({
        defaultPageSize: response?.data?.data?.per_page,
        current: response?.data?.data?.current_page,
        defaultCurrent: response?.data?.data?.current_page,
        total: response?.data?.data?.total,
      });
      setTableData(response?.data?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting daily finishing line plan data");
      setError("Could not load this list.");
    }
  }, [currentPage, filterValues]);

  const onAdd = () => {
    navigate("/digitalization/daily-finishing-line-plan/add");
  };

  const onEdit = (id) => {
    navigate(`/digitalization/daily-finishing-line-plan/edit/${id}`);
  };

  const onDelete = async (id) => {
    setLoading(true);
    const response = await deleteData(`${DAILY_LINE_PLAN_DELETE_API}/${id}`);

    if (response && response?.code === 200) {
      message.success(
        response?.message || "Daily Finishing Line Plan Deleted Successfully",
      );
      refetch();
    } else {
      message.error("Error Deleting Daily Finishing Line Plan");
    }
    setLoading(false);
  };

  // Effects
  useEffect(() => {
    getAppRoleMenu();
  }, [currentPage, filterValues, refresh, getAppRoleMenu]);

  const handleCopy = async(id) => {
    try {
      const response = await getData(`${DAILY_LINE_PLAN_LIST_COPY}/${id}`, false);
      console.log("🚀 ~ handleCopy ~ response:", response)
      if (response?.status === 200) {
        alertPop("success","Copied Successfully");
        getAppRoleMenu();
      } else {
        message.error("Error Copying the record");
      }
    } catch (error) {
      message.error("Error Copying the record");
    }
  }

  const tableColumns = [
    {
      title: "Date",
      dataIndex: "plan_date",
      key: "plan_date",
      align: "center",
      width: "7%",
      render: (row) => cglFormatDate(row),
    },
    {
      title: "Line",
      dataIndex: ["finishing_line_info", "name"],
      key: "finishing_line_info_name",
      align: "center",
      width: "7%",
    },
    {
      title: "Style",
      dataIndex: ["style_info", "style_no"],
      key: "style_info_style_no",
      width: "12%",
    },
    {
      title: "Sub Style",
      dataIndex: ["sub_style_info", "sub_style_no"],
      key: "sub_style_info_sub_style_no",
      width: "12%",
      render: (row) => row || "",
    },
    {
      title: "Sales Contract",
      dataIndex: ["sales_contract", "reference_no"],
      key: "sales_contract_reference_no",
      width: "12%",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer_info", "name"],
      key: "buyer_info_name",
      render: (row) => row || "",
    },
    {
      title: "Buying Office",
      dataIndex: ["buying_office_info", "name"],
      key: "buying_office_info_name",
      render: (row) => row || "",
    },
    {
      title: "PO",
      dataIndex: ["po_map_info", "original_po"],
      key: "po_map_info_original_po",
      render: (row) => row || "",
    },
    {
      title: "Color",
      dataIndex: ["color_info", "name"],
      key: "color_info_name",
      render: (row) => row || "",
    },
    {
      title: "Size",
      dataIndex: ["size_info", "name"],
      key: "size_info_name",
      render: (row) => row || "",
    },
    {
      title: "Supervisor",
      dataIndex: ["supervisor_info", "name"],
      key: "supervisor_info_name",
      render: (row, record) =>
        record?.supervisor_info?.additional_info?.id_card_no
          ? `${record.supervisor_info.additional_info.id_card_no} (${record.supervisor_info.name})`
          : record?.supervisor_info?.name || "",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      align: "center",
      width: "7%",
    },
    {
      title: "Factory",
      dataIndex: ["sub_contract_factory_info", "name"],
      key: "sub_contract_factory_info_name",
      render: (row) => row || "",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            onClick={() => onEdit(record?.id)}
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <EditTwoTone />
                        </span>
                        <span style={{ marginLeft: 5 }}>Edit</span>
                      </div>
                    ),
                    key: "2",
                    onClick: () => onEdit(record?.id),
                    disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
                  },
                  {
                    label: (
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <CopyOutlined style={{ color: "#1890ff" }} />
                        </span>
                        <span style={{ marginLeft: 5 }}>Copy</span>
                      </div>
                    ),
                    key: "4",
                    onClick: () => handleCopy(record.id),
                    disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
                    style: {
                      display: isCurrentDate(record?.plan_date)
                        ? "none"
                        : "block",
                    },
                  },
                  {
                    label: (
                      <div
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span style={{ marginLeft: 5 }}>Delete</span>
                      </div>
                    ),
                    key: "3",
                    onClick: () => onDelete(record?.id),
                    disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={(values) => {
          setCurrentPage(1);
          setFilterValues(values || {});
        }}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="digitalization_daily_finishing_line_plan"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onAdd}
                disabled={!hasPermission([NO_PERMISSION_REQUIRED])}
                addButtonText={
                  hasPermission([NO_PERMISSION_REQUIRED])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                hasImportButton={hasPermission([
                  DAILY_LINE_PLAN_IMPORT_PERMISSION,
                ])}
                acceptedFileTypes=".xlsx"
                uploadAPI={ImportDailyLinePlan}
              />
            }
            title={
              <Typography.Title level={5}>
                Daily Finishing Line Plan
              </Typography.Title>
            }
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                className="DailyLinePlanList-table-responsive compact-table"
                scroll={{ x: 1300, y: 600 }}
                rowKey={(record) => record?.id}
                size="small"
                bordered
                loading={loading}
                error={error}
                onRetry={() => getAppRoleMenu()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns.map((col, idx) => ({
                  ...col,
                  fixed: idx < 4 ? "left" : undefined,
                }))}
                sticky
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default DailyFinishingLinePlan;
